import { customFetch } from '../../services/apiService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    'Content-Type': 'application/json'
  };
};

export const fetchSetsList = async (endpoint: string, params: Record<string, string | number>) => {
  const urlParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      urlParams.set(key, String(value));
    }
  });
  
  const queryString = urlParams.toString();
  const url = `${endpoint}${queryString ? `?${queryString}` : ''}`;
  
  const res = await customFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể tải danh sách bộ câu hỏi.');
  }
  
  const rawList = Array.isArray(data.data) ? data.data : Array.isArray(data.items) ? data.items : [];
  const normalizedSets = rawList.map((item: any) => ({
    ...item,
    id: String(item._id || item.id || ''),
    _id: String(item._id || item.id || ''),
    name: item.title || item.name || 'Bộ câu hỏi',
    title: item.title || item.name || 'Bộ câu hỏi',
    subject: item.subject || 'Khác',
    grade: String(item.grade || '4'),
    totalQuestions: item.totalQuestions ?? item.questionCount ?? 0,
    questionCount: item.questionCount ?? item.totalQuestions ?? 0,
    isPublic: Boolean(item.isPublic)
  }));
  
  return {
    sets: normalizedSets,
    total: data.pagination?.total ?? data.total ?? normalizedSets.length,
    quota: data.quota
  };
};

export const useMyQuestionSets = (params: any) => {
  return useQuery({
    queryKey: ['myQuestionSets', params],
    queryFn: () => fetchSetsList('/api/v1/questions/sets/me', params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData, // keepPreviousData logic for v5
  });
};

export const usePublicQuestionSets = (params: any) => {
  return useQuery({
    queryKey: ['publicQuestionSets', params],
    queryFn: () => fetchSetsList('/api/v1/questions/sets/public', params),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

export const useQuestionSetDetail = (setId: string) => {
  return useQuery({
    queryKey: ['questionSetDetail', setId],
    queryFn: async () => {
      const res = await customFetch(`/api/v1/questions/sets/${setId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Không thể tải chi tiết bộ câu hỏi.');
      }
      
      const setInfo = data.data || data.set || {};
      const qs = Array.isArray(setInfo.questions) 
        ? setInfo.questions 
        : Array.isArray(data.questions) 
          ? data.questions 
          : [];
          
      return {
        setInfo: {
          ...setInfo,
          id: String(setInfo._id || setInfo.id || ''),
          _id: String(setInfo._id || setInfo.id || ''),
          name: setInfo.title || setInfo.name || 'Bộ câu hỏi',
          title: setInfo.title || setInfo.name || 'Bộ câu hỏi',
        },
        questions: qs,
        quota: data.quota
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!setId
  });
};

export const useDeleteQuestionSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (setId: string) => {
      const res = await customFetch(`/api/v1/questions/sets/${setId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Lỗi khi xóa bộ câu hỏi.');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myQuestionSets'] });
      queryClient.invalidateQueries({ queryKey: ['publicQuestionSets'] });
    }
  });
};

export const useCloneQuestionSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (setId: string) => {
      const res = await customFetch(`/api/v1/questions/sets/${setId}/clone`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Lỗi khi nhân bản bộ câu hỏi.');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myQuestionSets'] });
      queryClient.invalidateQueries({ queryKey: ['publicQuestionSets'] });
    }
  });
};

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (qId: string) => {
      const res = await customFetch(`/api/v1/questions/${qId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Lỗi khi xóa câu hỏi.');
      return data;
    },
    onSuccess: (_, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['questionSetDetail'] });
      queryClient.invalidateQueries({ queryKey: ['myQuestionSets'] });
      queryClient.invalidateQueries({ queryKey: ['publicQuestionSets'] });
    }
  });
};

export const useMediaLibrary = () => {
  return useQuery({
    queryKey: ['mediaLibrary', 'class/questions'],
    queryFn: async () => {
      const res = await customFetch(`/api/v1/media/list?folder=class/questions`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Không thể tải thư viện ảnh.');
      }
      return data.files || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useUploadMedia = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1200,
        useWebWorker: true
      };
      const compressedFile = await imageCompression(file, options);
      
      const formData = new FormData();
      formData.append('file', compressedFile, file.name);
      formData.append('folder', 'class/questions');
      
      const token = localStorage.getItem('token');
      
      const res = await customFetch('/api/v1/media/upload?folder=class/questions', {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: formData
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Lỗi khi tải ảnh lên.');
      }
      
      return data.url;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mediaLibrary'] });
    }
  });
};
